import { extInSite } from './IvrShared';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { ZoomTable, ZoomBtn, ZoomModal, ZoomField, ZoomBadge } from './zoom/ZoomTable';
import { useCustomer } from './customerCtx';
import { oooScheduleText } from './OooScheduleEditor';

/* Voicemail (Phone System Management). Administrators see every mailbox of the
   organisation with message COUNTS only — never the messages themselves — and
   can switch voicemail on/off, set the e-mail copy and reset the PIN. Out of office,
   forwarding and greetings are edited in one place: Users › Call handling (2026-10-25;
   removed from this page 2026-10-28 at TEC's request).
   Users manage their own messages in the CallEssential app. */
const err = (e: any) => e?.response?.data?.error || e?.message || 'Something went wrong';
const fmt = (v: any) => (v ? new Date(v).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const ACTIONS: Record<string, string> = { vm: 'Take a voicemail', forward: 'Forward to a number', message: 'Play a message and hang up' };

export default function VoicemailPage({ site }: { site?: any } = {}) {
  const cust = useCustomer();
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [edit, setEdit] = useState<any>(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [loadErr, setLoadErr] = useState('');

  const load = () => {
    setLoading(true);
    axios.get('/api/voicemail/mailboxes').then((r) => { setRows(Array.isArray(r.data) ? r.data : []); setLoadErr(''); })
      .catch((e) => { setRows([]); setLoadErr(e?.response?.status === 403 ? 'Voicemail was recently added to your account. Please sign out and sign in again to see the mailboxes.' : err(e)); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const inSite = (ext: any) => extInSite(ext, site);
  const visible = rows.filter((r) => inSite(r.extension_number) && cust.match('user', r.user_id)
    && (!search || `${r.extension_number} ${r.display_name || ''} ${r.username || ''} ${r.first_name || ''} ${r.last_name || ''}`.toLowerCase().includes(search.toLowerCase())));

  const open = (r: any) => {
    setMsg('');
    setEdit({ ...r, email: r.vm_email || r.user_email || '', newPin: '' });
  };
  const quickToggle = async (r: any) => {
    try {
      const res = await axios.put(`/api/voicemail/mailboxes/${r.extension_id}`, { enabled: !r.enabled });
      if (res.data.pin) alert(`Voicemail switched on for ${r.extension_number}. PIN: ${res.data.pin} (the user can change it in the CallEssential app).`);
      load();
    } catch (e) { alert(err(e)); }
  };
  const save = async () => {
    setBusy(true); setMsg('');
    try {
      const e = edit;
      const r = await axios.put(`/api/voicemail/mailboxes/${e.extension_id}`, { enabled: !!e.enabled, email: e.email || '', email_enabled: !!e.email_enabled });
      let pin = r.data.pin || '';
      setEdit(null); load();
      if (pin) alert(`Voicemail switched on for ${e.extension_number}. PIN: ${pin}`);
    } catch (x) { setMsg(err(x)); }
    setBusy(false);
  };
  const resetPin = async () => {
    try { const r = await axios.put(`/api/voicemail/mailboxes/${edit.extension_id}`, { reset_pin: true }); setEdit({ ...edit, newPin: r.data.pin }); }
    catch (x) { setMsg(err(x)); }
  };
  const setO = (k: string, v: any) => setEdit({ ...edit, ooo: { ...edit.ooo, [k]: v } });

  const oooText = (o: any) => (o.active ? `On now — ${ACTIONS[o.action] || o.action}` : o.enabled ? `Scheduled ${fmt(o.start_at)}`
    : o.schedule_enabled && (o.schedule || []).length ? `Schedule: ${oooScheduleText(o.schedule)}` : 'Off');

  return (
    <>
      {edit && (
        <ZoomModal width={760} title={`Voicemail — ${edit.extension_number} ${[edit.first_name, edit.last_name].filter(Boolean).join(' ') || edit.display_name || ''}`}
          onClose={() => !busy && setEdit(null)}
          footer={<><ZoomBtn onClick={() => setEdit(null)}>Cancel</ZoomBtn><ZoomBtn variant="primary" onClick={() => !busy && save()}>{busy ? 'Saving…' : 'Save'}</ZoomBtn></>}>
          {msg && <div style={{ color: '#b91c1c', marginBottom: 10 }}>{msg}</div>}
          <h4 style={{ margin: '0 0 8px' }}>Mailbox</h4>
          <div className="z-field-grid">
            <ZoomField label="Voicemail"><label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" style={{ width: 'auto' }} checked={!!edit.enabled} onChange={(e) => setEdit({ ...edit, enabled: e.target.checked })} /> On for this user</label></ZoomField>
            <ZoomField label="Messages" hint="Counts only — messages are private to the user"><input disabled value={`${edit.new_count} new · ${edit.old_count} old · last ${fmt(edit.last_message_at)}`} /></ZoomField>
            <ZoomField label="E-mail a copy of each message to"><input value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} placeholder="user@company.com" /></ZoomField>
            <ZoomField label="E-mail copy"><label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" style={{ width: 'auto' }} checked={!!edit.email_enabled} onChange={(e) => setEdit({ ...edit, email_enabled: e.target.checked })} /> Send the recording by e-mail</label></ZoomField>
            <ZoomField label="PIN" hint="Used when dialling *97"><div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><ZoomBtn onClick={resetPin}>Reset PIN</ZoomBtn>{edit.newPin && <b>New PIN: {edit.newPin}</b>}</div></ZoomField>
          </div>
          <h4 style={{ margin: '16px 0 8px' }}>Out of office, forwarding and greetings</h4>
          {!edit.user_id ? <div style={{ color: '#6b7280' }}>This extension has no user.</div> : (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13 }}>
              <span>Out of office: <b>{oooText(edit.ooo)}</b></span>
              <span style={{ color: '#6b7280' }}>Change it under Users › Call handling.</span>
            </div>)}
        </ZoomModal>
      )}

      <ZoomTable title="Voicemail" subtitle="Every mailbox of your organisation — message counts only. Users listen to their own messages in the CallEssential app or by dialling *97."
        search={search} onSearch={setSearch} searchPlaceholder="Search extension or name"
        columns={[
          { key: 'ext', label: 'Extension', render: (r: any) => <div><div className="z-tbl-primary-cell">{r.extension_number}</div><div className="z-tbl-sub">{[r.first_name, r.last_name].filter(Boolean).join(' ') || r.display_name || r.username || '—'}</div></div> },
          ...(cust.enabled ? [{ key: 'cust', label: 'Customer', render: (r: any) => cust.nameOf(cust.tagOf('user', r.user_id)) || '—' }] : []),
          { key: 'vm', label: 'Voicemail', render: (r: any) => <button className={`z-badge ${r.enabled ? 'z-badge-green' : 'z-badge-gray'}`} style={{ border: 'none', cursor: 'pointer' }} onClick={() => quickToggle(r)}>{r.enabled ? 'On' : 'Off'}</button> },
          { key: 'new', label: 'New', render: (r: any) => (r.enabled ? <b style={{ color: r.new_count ? '#b45309' : undefined }}>{r.new_count}</b> : '—') },
          { key: 'old', label: 'Old', render: (r: any) => (r.enabled ? String(r.old_count) : '—') },
          { key: 'last', label: 'Last message', render: (r: any) => (r.enabled ? fmt(r.last_message_at) : '—') },
          { key: 'mail', label: 'E-mail copy', render: (r: any) => (r.enabled ? (r.email_enabled ? (r.vm_email || r.user_email || '—') : 'Off') : '—') },
          { key: 'ooo', label: 'Out of office', render: (r: any) => <ZoomBadge text={!r.ooo.active && r.after_hours ? 'After hours' : oooText(r.ooo)} tone={r.ooo.active || r.after_hours ? 'blue' : 'gray'} /> },
        ]}
        rows={visible} rowKey={(r: any) => r.extension_id} selectable={false}
        rowActions={(r: any) => <>
          <ZoomBtn onClick={() => open(r)}>Configure</ZoomBtn>
        </>}
        emptyText={loading ? 'Loading…' : (loadErr || (rows.length ? 'No mailboxes match this customer, site or search' : 'No extensions'))} />
    </>
  );
}
